import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { coldHandoffStatusSelector } from './coldHandoffSelectors';

import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import { disableSpawnCall } from 'features/spawn-call/store';
import type { RootState } from 'features/app/store/store';
import { AudioPlayer } from 'features/audio-player/services';
import { LogoutType } from 'features/call/call-base/enums';
import { CallDaoService } from 'features/call/call-base/services';
import {
  handleResetCall,
  setCallTakenTime,
  setRelayCallState,
} from 'features/call/call-base/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { ColdHandoffStatus } from 'features/cold-handoff/enums';
import type { ColdHandoffState } from 'features/cold-handoff/interfaces';
import { ColdHandoffDaoService } from 'features/cold-handoff/services';
import { handleError } from 'features/notification/store';
import {
  removeSession,
  resetSessionId,
  rnsConnectionIdSelector,
  sessionIdSelector,
} from 'features/session/store';
import { userIdSelector } from 'features/user/store';
import { toIsoWithTimezone } from 'features/utils/helpers';
import { resetVriChat } from 'features/vri-chat/store';
import { incomingCallAccepted } from 'features/caller-session/store';
import { deafPhoneNumberSelector } from 'features/call/call-deaf/store';
import { acceptColdHandoffOffer } from 'features/call/call-ui-state/store';
import { SipSessionEventBus } from 'features/sip/services';
import { sendAnalyticsInfo } from 'features/analytics/helpers';

export const initialColdHandoffState: ColdHandoffState = {
  status: ColdHandoffStatus.NONE,
  requestingUser: {
    name: '',
  },
  acceptingUser: {
    uri: '',
  },
  isRequestor: false,
};

export const coldHandoffSlice = createSlice({
  name: 'coldHandoff',
  initialState: initialColdHandoffState,
  reducers: {
    coldHandoffRequestReceived: (
      state,
      action: PayloadAction<{ requestingUserName: string }>
    ) => {
      state.status = ColdHandoffStatus.REQUESTED;
      state.requestingUser = { name: action.payload.requestingUserName };
    },
    coldHandoffAcceptedByUser: (
      state,
      action: PayloadAction<{ acceptingUserUri: string }>
    ) => {
      state.status = ColdHandoffStatus.ACCEPTED;
      state.acceptingUser = { uri: action.payload.acceptingUserUri };
    },
    coldHandoffCanceledByAcceptingUser: (state) => {
      state.status = ColdHandoffStatus.REQUESTED;
      state.acceptingUser = { uri: '' };
    },
    coldHandoffCanceledByRequestor: () => initialColdHandoffState,
    requestColdHandoff: (state) => {
      state.status = ColdHandoffStatus.REQUESTED;
      state.isRequestor = true;
    },
    coldHandoffCompleted: (state) => {
      state.status = ColdHandoffStatus.COMPLETED;
    },
    resetColdHandoff: () => initialColdHandoffState,
  },
});

export const {
  coldHandoffRequestReceived,
  coldHandoffAcceptedByUser,
  coldHandoffCanceledByAcceptingUser,
  coldHandoffCanceledByRequestor,
  requestColdHandoff,
  resetColdHandoff,
  coldHandoffCompleted,
} = coldHandoffSlice.actions;

export const coldHandoffReducer = coldHandoffSlice.reducer;

export const takeColdHandoffRequest = createAsyncThunk(
  'cold-handoff/take',
  async (payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const userId = userIdSelector(state);
      const sessionId = rnsConnectionIdSelector(state);
      const deafPhoneNumber = deafPhoneNumberSelector(state);
      const call = await CallDaoService.takeCall({
        callId,
        userId,
        sessionId,
      });
      dispatch(incomingCallAccepted(callId.toString(), deafPhoneNumber));
      dispatch(setRelayCallState(call.CallState));
      dispatch(
        setCallTakenTime(toIsoWithTimezone(call.TakenOrAbandonDateTime))
      );
      dispatch(acceptColdHandoffOffer());
      dispatch(resetColdHandoff());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'takeColdHandoffRequest',
        })
      );
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const declineColdHandoffRequest = createAsyncThunk(
  'cold-handoff/decline',
  async (payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      await CallDaoService.denyCall({
        callId,
      });
      await dispatch(
        removeSession({
          logoutType: LogoutType.Normal,
        })
      ).unwrap();
      dispatch(resetSessionId());
      dispatch(handleResetCall()).unwrap();
      dispatch(resetVriChat());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'declineColdHandoffRequest',
        })
      );
    } finally {
      AudioPlayer.stop();
    }
  }
);

// THIS is the step called by VI2 that transfers ownership of the call.
export const completeColdHandoffForRelay = createAsyncThunk(
  'cold-handoff/complete',
  async (payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    try {
      await ColdHandoffDaoService.completeHandoff({
        callId,
      });
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'completeColdHandoff',
        })
      );
    }
  }
);

export const forceCancelColdHandoff = createAsyncThunk(
  'cold-handoff/force-cancel',
  async (payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const coldHandoffStatus = coldHandoffStatusSelector(state);
    if (
      coldHandoffStatus === ColdHandoffStatus.REQUESTED ||
      coldHandoffStatus === ColdHandoffStatus.ACCEPTED
    ) {
      try {
        await ColdHandoffDaoService.cancelHandoff({ callId });
        dispatch(resetColdHandoff());
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'forceCancelColdHandoff',
          })
        );
      }
    }
  }
);

export const confirmColdHandoffTransfer = createAsyncThunk(
  'cold-handoff/confirm-transfer',
  async (payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const sessionId = sessionIdSelector(state);
      const acceptingUserUri = state.coldHandoff.acceptingUser.uri;
      SipSessionEventBus.refer$.next(acceptingUserUri);
      dispatch(coldHandoffCompleted());

      // This call the handoff state history to complete in Relay
      await ColdHandoffDaoService.transferHandoff({
        callId,
        sessionLoginHistoryId: sessionId,
      });
      VoiceMeetingEventBus.resetVoiceSession$.next();

      await dispatch(completeColdHandoffForRelay());
      dispatch(
        sendAnalyticsInfo({
          method: 'confirmColdHandoffTransfer',
          message:
            'Cold Handoff completed: ownership transferred to accepting VI. Returning to queue for next call.',
        })
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'confirmColdHandoffTransfer',
        })
      );
    } finally {
      dispatch(handleResetCall()).unwrap();
      dispatch(disableSpawnCall());
      dispatch(resetVriChat());
    }
  }
);
